/**
 * Programs
 * 8-week structured therapeutic programs.
 * Week position is determined by REAL elapsed time since enrollment.
 * Multiple programs can run simultaneously with independent clocks.
 * Re-enrollment archives the old run and starts a fresh one.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import { GriefIcon, AnxietyIcon, BurnoutIcon, IntegrationIcon, RedesignIcon } from '../components/HypIcons';

const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAL  = '#1E6B68';
const CREAM = 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 60%, #B8D3CF 100%)';

const PROGRAM_META = {
  grief: {
    Icon: GriefIcon,
    color: '#4A7B9D',
    desc: 'A gentle, body-first protocol for navigating grief and loss.',
    detail: 'Each week combines a somatic practice, a behavioural challenge, and a guided reflection. You cannot rush grief — but you can learn to move with it.',
  },
  anxiety: {
    Icon: AnxietyIcon,
    color: '#2D8B87',
    desc: 'Rewire the nervous system\'s alarm signal through somatic practice.',
    detail: 'Anxiety is not a character flaw — it\'s a nervous system pattern. Each week you train a specific regulation tool and apply it to a real situation in your life.',
  },
  burnout: {
    Icon: BurnoutIcon,
    color: '#B8906A',
    desc: 'Restore energy, identity and meaning after depletion.',
    detail: 'Burnout is a signal, not a weakness. This program rebuilds from the inside out — values, boundaries, energy systems, and your relationship with effort.',
  },
  integration: {
    Icon: IntegrationIcon,
    color: '#7B68A0',
    desc: 'From surviving to integrating — trauma held with care.',
    detail: 'Slow and titrated. Each week expands your window of tolerance slightly — never pushing, always with support. You don\'t have to revisit the trauma to integrate it.',
  },
  redesign: {
    Icon: RedesignIcon,
    color: '#1E6B68',
    desc: 'Architect the mind you want to live in.',
    detail: 'Identity change is not about willpower. Each week dismantles one layer of the old operating system and installs something new — through embodied practice, behavioural challenge, and deep reflection.',
  },
};

const STATIC_PROGRAMS = [
  { id: 'grief',       title: 'Grief & Loss',         subtitle: 'A gentle return to yourself after loss',        duration_weeks: 8 },
  { id: 'anxiety',     title: 'Anxiety & Overwhelm',  subtitle: "Rewire the nervous system's alarm signal",      duration_weeks: 8 },
  { id: 'burnout',     title: 'Burnout Recovery',     subtitle: 'Restore energy, identity and meaning',          duration_weeks: 8 },
  { id: 'integration', title: 'Trauma Integration',   subtitle: 'From surviving to integrating',                 duration_weeks: 8 },
  { id: 'redesign',    title: 'Redesign Your Mind',   subtitle: 'Architect the mind you want to live in',        duration_weeks: 8 },
];

// ── Weekly behavioral challenges ───────────────────────────────────────────────
const WEEK_CHALLENGES = {
  grief: [
    "Take 10 uninterrupted minutes today and write freely about what you've lost — without editing, without explaining, without making it make sense. Just let it out on paper.",
    "Find the place in your body where this grief lives. Each day this week, place your hand there and breathe into it for 2 minutes. Don't try to fix it — just be with it.",
    "Write yourself a permission slip — literally. 'I give myself permission to feel ___.' Put it somewhere you'll see it. This week, when that feeling surfaces, don't push it away.",
    "Write two separate letters about the same loss: one from anger, one from love. Let both be completely, unapologetically true. Don't soften either.",
    "Create one small ritual this week to honour a memory — a photo you look at, a place you visit, a song you play consciously. Do it with full attention.",
    "Write a letter from the person or thing you lost — to you. What would they say? What would they want for you now? Let yourself be surprised.",
    "Do one thing this week that you stopped doing. Something small — a walk you used to take, a food you used to enjoy, a call you've been putting off. Just one.",
    "Write a commitment to yourself: how you will carry this grief and still live fully. Not a vow to stop grieving — a vow to let life continue alongside it.",
  ],
  anxiety: [
    "For the next 7 days, observe your anxiety without trying to fix it. Describe it in a notebook as if it were a character — what it looks like, what it wants, what it fears.",
    "Practice the physiological sigh (double inhale through the nose, slow exhale) every time you notice tension this week. At the end of each day, count how many times you used it.",
    "Every morning this week, spend 2 minutes with the 5-4-3-2-1 method: 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, 1 you can taste. Do it slowly.",
    "When an anxious thought appears this week, practise labelling it: 'I notice I'm having the thought that...' Don't argue with it. Don't fix it. Just name it and watch what happens.",
    "Identify one thing you've been avoiding because of anxiety. This week, take one small step toward it — not the whole thing, just one step closer. Log what happened before, during, and after.",
    "This week, treat sleep as a performance asset. No screens 1 hour before bed, same wake time every day. Track your anxiety level the morning after vs. normal nights.",
    "In one social interaction this week, say something slightly more honest than you normally would. Observe what happens — in them, and in you.",
    "Write a letter to your anxiety — thanking it for what it tried to protect you from, and telling it clearly what you no longer need it to guard.",
  ],
  burnout: [
    "Do a full energy audit today: rate your physical, emotional, creative, and relational energy from 1–10. Be brutally honest. Then write: where did the depletion start?",
    "Schedule one block of genuine rest this week — not Netflix, not social media. Silence, a walk, unstructured time. Protect it as if it were a non-negotiable meeting.",
    "Write every single thing currently taking your energy. Then mark each item: 'mine to carry' or 'not mine to carry.' Don't justify — just sort.",
    "Spend 30 minutes this week doing something completely useless — something you enjoy with zero productive output. Notice every impulse to make it useful. Don't obey them.",
    "Practise saying no once this week — to something you would normally say yes to out of obligation or guilt. Observe the discomfort. Write about it after.",
    "Write a description of work that would feel genuinely meaningful to you — not tolerable, not acceptable, but alive. Be specific about what's in it and what's not.",
    "Design your ideal week from scratch — waking to sleeping, every day. Include everything that nourishes you. Then map the gap: what would it take to move 10% closer?",
    "Write a commitment letter to yourself about how you will work and live from here. Specific. Honest. With real consequences if you break it.",
  ],
  integration: [
    "Each morning this week, before anything else: pause and ask 'How is my body right now?' No analysis. Just three words — whatever comes. Write them down.",
    "Track your window of tolerance this week. When do you feel expanded (present, connected, open)? When contracted (overwhelmed, shut down, numb)? Just observe — don't judge.",
    "Choose one small safe memory from before the difficult period. Each day this week, spend 3 minutes holding it in your mind. Notice what happens in the body — not what you think, what you feel.",
    "Find a place in your body that feels neutral or slightly at ease right now. Spend 2 minutes there each day this week, just noticing. No agenda. Just presence.",
    "Write 3 sentences beginning with 'What happened was...' (facts only). Then 3 sentences beginning with 'What I made it mean was...' Notice the gap between the two.",
    "Identify one part of you that has been working very hard to keep you safe — maybe through anger, numbness, hypervigilance, or control. Write that part a short note of acknowledgement.",
    "Make a list: 5 ways you are different from who you were before. Include things that feel unexpected, uncomfortable, or even paradoxically good. Let the list be honest.",
    "Create a small ritual to mark the end of this program. Private, personal — a walk, a ceremony, a letter, a place you visit. Something that says: I am stepping forward now.",
  ],
  redesign: [
    "Write down the 5 beliefs you currently operate from — not what you think you should believe, but what actually runs your daily decisions. Be precise. Be honest. No one else will read this.",
    "Take one limiting belief from last week's list and trace it to its exact origin. Who was there? How old were you? What happened? Write the scene in detail, as if explaining it to someone else.",
    "Spend one full day acting AS your future self. Make every decision they would make. Speak as they would. Handle friction as they would. At the end of the day, write what felt natural — and what felt like acting.",
    "Write your identity declaration: 3–5 statements beginning with 'I am...' — slightly beyond where you are, but believable. Say them out loud in front of a mirror, every morning this week. Notice every trace of resistance.",
    "This week, each time an emotion pulls you back toward the old self, pause. Name the emotion. Identify the old belief it's protecting. Don't obey it — write it down instead.",
    "Identify one relationship or social context where your old identity shows up most strongly. This week, respond once — just once — as your new self would. Even slightly differently.",
    "Make one concrete change to your physical environment: remove something that signals the old identity, or deliberately add something that signals the new one. Make it visible.",
    "Write a letter from your future self — one year from now — to the person you were at the start of this program. Write it as if the transformation has already happened. Read it aloud.",
  ],
};

const WEEK_PROMPTS = {
  grief: [
    "What came up when you wrote without filter? What surprised you — about what you wrote, or about yourself?",
    "Where in your body do you feel this loss most? What did it feel like to simply be with it, without trying to change it?",
    "What have you been telling yourself you're not allowed to feel? What happened when you gave yourself permission?",
    "What did writing from both anger and love reveal? Can both be true at the same time — and what does it mean if they are?",
    "What meaning, if any, are you beginning to find? What would you want this experience to have taught you?",
    "What did their voice say in the letter? What surprised you about what they wanted for you?",
    "What was it like to re-enter something you'd stopped doing? Where did you feel slightly more yourself?",
    "How has carrying this grief changed you? What's different in you now — and what do you want to carry forward?",
  ],
  anxiety: [
    "What did you discover when you stopped fighting your anxiety and started observing it? What does it actually want?",
    "What did you notice about your breath and tension patterns this week? What happened when you interrupted them?",
    "Describe a moment this week when you felt genuinely safe and regulated. What specifically made it possible?",
    "What story does your anxiety most often tell you? What happened when you named it instead of believing it?",
    "What did you move toward this week? What did it feel like before, during, and after that one step?",
    "What changed in your anxiety when you protected your recovery? What does that tell you about your baseline?",
    "What happened when you were slightly more honest than usual? What did you expect — and what actually occurred?",
    "How has your relationship with anxiety shifted? What does it mean to understand something that used to control you?",
  ],
  burnout: [
    "Describe your current energy level honestly — physical, emotional, creative. Where did the depletion actually start?",
    "When did you last feel genuinely rested? What specifically made it possible — and what stands between you and that now?",
    "What matters most to you beneath all the roles? What was on the 'not mine to carry' list that surprised you?",
    "Who are you without your productivity? What came up when you tried to be useless for 30 minutes?",
    "What happened when you said no? What did the discomfort feel like — and what did it reveal about why you normally say yes?",
    "What would work feel like if it were meaningful? Be specific — what's in it, what's not, and what's the gap from now?",
    "What's missing from your current week that your designed week had? What would it take to move 10% closer to that?",
    "What are you committing to? Write the specific change — and what it means if you don't follow through.",
  ],
  integration: [
    "How is your body right now — not what you think, what you actually feel? What does even a small sense of safety feel like?",
    "Describe your window of tolerance this week. What expanded it? What contracted it? What patterns do you notice?",
    "What arose during the memory practice? What happened in the body when you visited something safe?",
    "What did you find when you looked for a place of ease in the body? What does neutral actually feel like?",
    "What sense are you beginning to make of what happened? Notice the gap between the facts and the meaning you gave them.",
    "Is there a part of you that has kept you safe — through control, anger, distance, or numbness? What would you want to say to that part?",
    "What has changed, however subtly, in how you see yourself? Include the unexpected things on your list.",
    "Write a few words to yourself — from yourself — as you step forward carrying this. What do you want to remember most?",
  ],
  redesign: [
    "What belief about yourself has been running quietly in the background — the one you've never really questioned? Write it exactly as it sounds in your head.",
    "Where did this story come from? Whose voice does it sound like — and when did you first decide it was true about you?",
    "Who are you becoming? Describe them in the first person, present tense. What did acting as them for one day show you?",
    "What did it feel like to declare who you are becoming? Where did resistance show up — and what exactly did it say?",
    "What emotional state do you want to access more consistently? What specific thing triggers the opposite — and what old belief is underneath that trigger?",
    "How does your old identity show up in your relationships — in what you say, don't say, or silently allow? What happened when you responded differently?",
    "What in your environment currently signals the old identity? What one change did you make — and what did you notice after?",
    "Read the letter back. What does your future self want you to know — and believe — right now, at this exact point?",
  ],
};


const MIN_REFLECTION = 80;

// ── Lock icon ────────────────────────────────────────────────────────────────
function LockIcon({ size = 14, color = '#AAB8B6' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <rect x="3" y="6" width="8" height="6" rx="1.5"
        stroke={color} strokeWidth="1.3" fill="none"/>
      <path d="M5 6V4.5a2 2 0 0 1 4 0V6"
        stroke={color} strokeWidth="1.3" strokeLinecap="round" fill="none"/>
      <circle cx="7" cy="9" r="0.8" fill={color}/>
    </svg>
  );
}

// ── Week card ────────────────────────────────────────────────────────────────
function WeekCard({ week, weekData, programId, isCurrent, isCompleted, isLocked, daysUntilUnlock, enrollmentId, onSaveReflection, reflectedWeeks }) {
  const weekNavigate = useNavigate();
  const [open, setOpen]           = useState(isCurrent);
  const storageKey                = `${programId}_week${week}_reflection`;
  const [reflection, setReflection] = useState(() => localStorage.getItem(storageKey) || '');
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [safetyMsg, setSafetyMsg] = useState(null);
  const [safetyLevel, setSafetyLevel] = useState('green');

  const prompt           = WEEK_PROMPTS[programId]?.[week - 1] || null;
  const savedReflection  = !isCurrent ? localStorage.getItem(storageKey) : null;
  const canSave          = reflection.trim().length >= MIN_REFLECTION;
  const hasReflected     = reflectedWeeks?.includes(week);

  // Safety check (debounced, silent)
  useEffect(() => {
    if (reflection.trim().length < 40) { setSafetyMsg(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/safety/check`, { text: reflection });
        if (res.data.level !== 'green') {
          setSafetyMsg(res.data.message);
          setSafetyLevel(res.data.level);
        } else { setSafetyMsg(null); setSafetyLevel('green'); }
      } catch { /* silent */ }
    }, 1800);
    return () => clearTimeout(timer);
  }, [reflection]);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    localStorage.setItem(storageKey, reflection.trim());
    await onSaveReflection?.(reflection.trim());
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // ── Locked week ─────────────────────────────────────────────────────────
  if (isLocked) {
    return (
      <div style={{
        borderRadius: '14px',
        border: '1.5px solid rgba(0,0,0,0.06)',
        background: 'rgba(255,255,255,0.35)',
        marginBottom: '10px',
        padding: '14px 20px',
        display: 'flex', alignItems: 'center', gap: '14px',
        opacity: 0.65,
      }}>
        <div style={{
          width: '34px', height: '34px', borderRadius: '50%',
          background: '#F0F0F0',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <LockIcon size={14} color="#AABAB8" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.08em', color: '#AABAB8', textTransform: 'uppercase' }}>
              Week {week}
            </span>
          </div>
          <p style={{ margin: 0, fontWeight: '600', color: '#AABAB8', fontSize: '0.9rem' }}>{weekData.theme}</p>
        </div>
        <span style={{ fontSize: '0.72rem', color: '#AABAB8', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {daysUntilUnlock === 1 ? 'tomorrow' : `in ${daysUntilUnlock}d`}
        </span>
      </div>
    );
  }

  // ── Unlocked (current or past) ───────────────────────────────────────────
  return (
    <div style={{
      borderRadius: '14px',
      border: `1.5px solid ${isCurrent ? TEAL : isCompleted ? 'rgba(30,107,104,0.2)' : 'rgba(0,0,0,0.07)'}`,
      background: isCurrent ? 'rgba(30,107,104,0.04)' : isCompleted ? 'rgba(30,107,104,0.02)' : 'rgba(255,255,255,0.5)',
      marginBottom: '12px',
      overflow: 'hidden',
      transition: 'all 0.2s',
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '16px 20px',
          background: 'transparent', border: 'none',
          display: 'flex', alignItems: 'center', gap: '14px',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{
          width: '36px', height: '36px', borderRadius: '50%',
          background: isCompleted ? TEAL : isCurrent ? 'rgba(30,107,104,0.15)' : '#F0F0F0',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {isCompleted
            ? <span style={{ color: '#fff', fontSize: '0.9rem' }}>✓</span>
            : <span style={{ color: isCurrent ? TEAL : '#AAA', fontSize: '0.8rem', fontWeight: '700' }}>{week}</span>
          }
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.08em', color: TEAL, textTransform: 'uppercase' }}>
              Week {week}
            </span>
            {isCurrent && (
              <span style={{ fontSize: '0.65rem', background: TEAL, color: '#fff', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                Active
              </span>
            )}
            {isCompleted && hasReflected && (
              <span style={{ fontSize: '0.65rem', background: 'rgba(30,107,104,0.1)', color: TEAL, padding: '2px 8px', borderRadius: '10px' }}>
                Reflected ✓
              </span>
            )}
          </div>
          <p style={{ margin: '2px 0 0', fontWeight: '600', color: '#1E3A3A', fontSize: '0.92rem' }}>{weekData.theme}</p>
          <p style={{ margin: '2px 0 0', color: '#666', fontSize: '0.8rem' }}>{weekData.focus}</p>
        </div>

        <span style={{ color: '#AAB8B6', fontSize: '0.8rem', transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>▼</span>
      </button>

      {open && (
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ borderTop: '1px solid rgba(30,107,104,0.1)', paddingTop: '16px' }}>

            {/* Challenge (current week) */}
            {isCurrent && WEEK_CHALLENGES[programId]?.[week - 1] && (
              <div style={{
                marginBottom: 20, padding: '16px 18px',
                background: 'linear-gradient(135deg, rgba(184,144,106,0.08) 0%, rgba(240,165,0,0.06) 100%)',
                borderRadius: 14, border: '1.5px solid rgba(184,144,106,0.25)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1.5L8.5 5.5H12.5L9.5 8L10.5 12L7 9.5L3.5 12L4.5 8L1.5 5.5H5.5L7 1.5Z"
                      stroke="#B8906A" strokeWidth="1.2" strokeLinejoin="round" fill="rgba(184,144,106,0.15)"/>
                  </svg>
                  <span style={{ fontSize: '0.68rem', fontWeight: '800', color: '#B8906A', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    This week's challenge
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#5A3E1E', lineHeight: 1.72 }}>
                  {WEEK_CHALLENGES[programId][week - 1]}
                </p>
              </div>
            )}

            {/* Practices */}
            <p style={{ fontSize: '0.68rem', fontWeight: '700', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
              Daily practices
            </p>
            {weekData.practices.map((p, i) => {
              const label = typeof p === 'string' ? p : p.label;
              const link  = typeof p === 'string' ? null : p.link;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: TEAL, opacity: 0.5, flexShrink: 0, marginTop: 7 }} />
                  {link ? (
                    <button
                      onClick={() => weekNavigate(link)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: '0.84rem', color: TEAL, lineHeight: 1.55, textDecoration: 'underline', textDecorationColor: 'rgba(30,107,104,0.25)', textUnderlineOffset: '3px' }}
                    >{label} →</button>
                  ) : (
                    <span style={{ fontSize: '0.84rem', color: '#5A7A77', lineHeight: 1.55 }}>{label}</span>
                  )}
                </div>
              );
            })}

            {/* Reflection (current + past weeks — optional, not a gate) */}
            {(isCurrent || isCompleted) && prompt && (
              <div style={{
                marginTop: 22, padding: '18px 16px',
                background: 'rgba(30,107,104,0.04)',
                borderRadius: 14, border: '1px solid rgba(30,107,104,0.15)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <p style={{ fontSize: '0.68rem', fontWeight: '700', color: TEAL, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                    Week reflection
                  </p>
                  {isCompleted && (
                    <span style={{ fontSize: '0.68rem', color: '#AABAB8' }}>optional · revisit anytime</span>
                  )}
                </div>
                <p style={{ fontSize: '0.88rem', color: '#3A6A67', lineHeight: 1.7, marginBottom: 14, marginTop: 0, fontStyle: 'italic' }}>
                  {prompt}
                </p>
                <textarea
                  value={reflection}
                  onChange={e => setReflection(e.target.value)}
                  placeholder="Write here — be honest, not polished."
                  rows={5}
                  style={{
                    width: '100%', padding: '12px 14px',
                    border: `1.5px solid ${canSave ? 'rgba(30,107,104,0.4)' : 'rgba(184,211,207,0.5)'}`,
                    borderRadius: 10, fontSize: '0.86rem', lineHeight: 1.65,
                    background: 'rgba(255,255,255,0.95)', color: '#1E3A39',
                    resize: 'vertical', fontFamily: 'inherit', outline: 'none',
                    boxSizing: 'border-box', transition: 'border-color 0.2s',
                  }}
                />

                {safetyMsg && (
                  <div style={{
                    marginTop: 10, padding: '10px 14px',
                    background: safetyLevel === 'red' ? 'rgba(163,59,43,0.06)' : 'rgba(184,144,106,0.08)',
                    border: `1px solid ${safetyLevel === 'red' ? 'rgba(163,59,43,0.25)' : 'rgba(184,144,106,0.30)'}`,
                    borderRadius: 10, fontSize: '0.82rem', lineHeight: 1.6,
                    color: safetyLevel === 'red' ? '#8B2C1E' : '#6B4A20',
                  }}>
                    <span style={{ fontWeight: 600 }}>A gentle note: </span>{safetyMsg}
                    {safetyLevel === 'red' && (
                      <div style={{ marginTop: 6 }}>
                        <a href="https://www.befrienders.org" target="_blank" rel="noopener noreferrer"
                          style={{ color: '#8B2C1E', fontWeight: 600, fontSize: '0.80rem' }}>
                          → befrienders.org — crisis support worldwide
                        </a>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontSize: '0.72rem', color: canSave ? TEAL : '#AABAB8', transition: 'color 0.3s' }}>
                    {saved ? '✓ Saved' : reflection.trim().length < MIN_REFLECTION
                      ? `${MIN_REFLECTION - reflection.trim().length} more characters to save`
                      : 'Ready to save'}
                  </span>
                  <button
                    onClick={handleSave}
                    disabled={!canSave || saving}
                    style={{
                      padding: '10px 22px',
                      background: canSave ? `linear-gradient(135deg, ${TEAL}, #2D8B87)` : 'rgba(184,211,207,0.3)',
                      color: canSave ? '#fff' : '#AABAB8',
                      border: 'none', borderRadius: 20,
                      fontSize: '0.85rem', fontWeight: '600',
                      cursor: canSave ? 'pointer' : 'not-allowed',
                      transition: 'all 0.25s',
                      boxShadow: canSave ? '0 3px 12px rgba(30,107,104,0.25)' : 'none',
                    }}
                  >
                    {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save reflection'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Program list card ─────────────────────────────────────────────────────────

// ── Program Outcome Card — presence + measured change ────────────────────────
// Completion reflects what the user actually did, not just elapsed time.
function ProgramOutcomeCard({ enrollmentId, color }) {
  const nav = useNavigate();
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!enrollmentId) return;
    axios.get(`${API}/programs/enrollment/${enrollmentId}/completion-report`)
      .then(r => setReport(r.data))
      .catch(() => {});
  }, [enrollmentId]);

  if (!report) return null;
  const pr = report.presence || {};
  const oc = report.outcomes || {};

  const outcomeRow = (label, o, lowerIsBetter = true) => {
    if (o?.baseline == null && o?.current == null) return null;
    const improved = o.delta != null && (lowerIsBetter ? o.delta < 0 : o.delta > 0);
    return (
      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid rgba(30,107,104,0.07)' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4A6A66' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', color: '#5A7A77' }}>
          {o.baseline != null ? o.baseline : '—'} → {o.current != null ? o.current : '—'}
          {o.delta != null && o.delta !== 0 && (
            <strong style={{ marginLeft: 8, color: improved ? '#1E6B68' : '#B8906A' }}>
              {o.delta > 0 ? '+' : ''}{o.delta}
            </strong>
          )}
        </span>
      </div>
    );
  };

  const rows = [
    outcomeRow('PHQ-9', oc.phq9, true),
    outcomeRow('GAD-7', oc.gad7, true),
    outcomeRow('ERI', oc.eri, false),
  ].filter(Boolean);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.9)', borderRadius: '18px',
      padding: '18px 20px', marginBottom: '16px',
      border: `1px solid ${color}22`, boxShadow: '0 2px 12px rgba(30,107,104,0.06)',
    }}>
      <p style={{ margin: '0 0 12px', fontSize: '0.68rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Your progress · measured
      </p>

      {/* Presence */}
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: rows.length ? 12 : 0 }}>
        <div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1E3A3A' }}>{pr.practice_days ?? 0}<span style={{ fontSize: '0.7rem', color: '#9AADAB', fontWeight: 500 }}>/{pr.days_elapsed ?? 0} days</span></div>
          <div style={{ fontSize: '0.65rem', color: '#8FA3A0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Practised</div>
        </div>
        <div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1E3A3A' }}>{pr.adherence_pct ?? 0}%</div>
          <div style={{ fontSize: '0.65rem', color: '#8FA3A0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Presence</div>
        </div>
        {pr.avg_regulation_delta != null && (
          <div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: pr.avg_regulation_delta >= 0 ? '#1E6B68' : '#B8906A' }}>
              {pr.avg_regulation_delta >= 0 ? '−' : '+'}{Math.abs(pr.avg_regulation_delta)}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#8FA3A0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg activation</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1E3A3A' }}>{pr.weeks_reflected ?? 0}<span style={{ fontSize: '0.7rem', color: '#9AADAB', fontWeight: 500 }}>/{report.total_weeks}</span></div>
          <div style={{ fontSize: '0.65rem', color: '#8FA3A0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reflections</div>
        </div>
      </div>

      {/* Outcomes */}
      {rows.length > 0 && <div>{rows}</div>}
      {rows.length === 0 && (
        <p style={{ margin: 0, fontSize: '0.75rem', color: '#9AADAB', lineHeight: 1.5 }}>
          No baseline measures yet — complete a clinical check-in so this program can show your change.
        </p>
      )}

      {/* Re-test call in the final week */}
      {report.retest_due && (
        <button
          onClick={() => nav('/assessment')}
          style={{
            marginTop: 14, width: '100%', padding: '12px',
            background: `linear-gradient(135deg, ${color}, ${color}CC)`,
            color: '#fff', border: 'none', borderRadius: '12px',
            fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Final week — re-test to see your change →
        </button>
      )}
    </div>
  );
}

function ProgramCard({ program, enrollment, onOpen }) {
  const meta    = PROGRAM_META[program.id] || {};
  const color   = meta.color || TEAL;
  const dayBadge = enrollment
    ? (enrollment.status === 'completed'
        ? 'Complete'
        : `Day ${enrollment.current_day} · Week ${enrollment.current_week}`)
    : null;

  return (
    <div
      onClick={() => onOpen(program, enrollment)}
      style={{
        background: 'rgba(255,255,255,0.9)', borderRadius: '16px',
        padding: '20px', marginBottom: '14px',
        boxShadow: '0 2px 12px rgba(30,107,104,0.08)',
        border: `1.5px solid ${enrollment ? color + '44' : 'rgba(184,211,207,0.25)'}`,
        cursor: 'pointer', transition: 'all 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,107,104,0.14)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(30,107,104,0.08)'; }}
    >
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '12px',
          background: color + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {meta.Icon && <meta.Icon size={26} color={color} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, color: '#1E3A3A', fontFamily: "'Playfair Display', serif", fontSize: '1.05rem' }}>
              {program.title}
            </h3>
            {dayBadge && (
              <span style={{
                fontSize: '0.65rem', background: color + '18', color, padding: '2px 8px',
                borderRadius: '10px', fontWeight: '700', whiteSpace: 'nowrap',
              }}>
                {dayBadge}
              </span>
            )}
          </div>
          <p style={{ margin: '0 0 12px', color: '#666', fontSize: '0.82rem', lineHeight: 1.5 }}>
            {meta.desc}
          </p>

          {enrollment ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.72rem', color: '#888' }}>
                  {enrollment.total_progress_pct || 0}% complete
                </span>
                <span style={{ fontSize: '0.72rem', color, fontWeight: '600' }}>
                  {enrollment.status === 'completed'
                    ? 'See your results →'
                    : enrollment.days_until_next_week > 0
                      ? `Week ${enrollment.current_week + 1} in ${enrollment.days_until_next_week}d`
                      : 'Final week'}
                </span>
              </div>
              <div style={{ height: '6px', background: '#F0F0F0', borderRadius: '3px' }}>
                <div style={{
                  height: '100%', width: `${enrollment.total_progress_pct || 0}%`,
                  background: color, borderRadius: '3px', transition: 'width 0.5s',
                }} />
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.78rem', color: '#AAB8B6' }}>8 weeks · somatic-first</span>
              <span style={{ fontSize: '0.78rem', color, fontWeight: '600' }}>View →</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Week list inside program detail ──────────────────────────────────────────
function ProgramWeeks({ programId, enrollment, color, onSaveReflection }) {
  const [weeks, setWeeks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/programs/${programId}`)
      .then(res => { if (res.data.weeks?.length) setWeeks(res.data.weeks); })
      .catch(() => {});
  }, [programId]);

  if (!weeks.length) return <div style={{ textAlign: 'center', color: '#AAB8B6', padding: '20px' }}>Loading…</div>;

  const isPreview     = !enrollment;                         // unenrolled — show as preview
  const currentWeek   = enrollment?.current_week  || 1;
  const weeksUnlocked = isPreview
    ? [1, 2, 3, 4, 5, 6, 7, 8]                              // all unlocked for preview
    : (enrollment?.weeks_unlocked || [1]);
  const completedWeeks = enrollment?.completed_weeks || [];
  const elapsedDays   = enrollment?.elapsed_days   || 0;

  return (
    <div>
      {weeks.map(w => {
        const isLocked    = !weeksUnlocked.includes(w.week);
        const isCurrent   = !isPreview && w.week === currentWeek && !isLocked;
        const isCompleted = !isPreview && w.week < currentWeek && !isLocked;
        // Days until this week unlocks = (week-1)*7 - elapsed_days
        const daysUntilUnlock = Math.max(1, ((w.week - 1) * 7) - elapsedDays);

        return (
          <WeekCard
            key={w.week}
            week={w.week}
            weekData={w}
            programId={programId}
            isCurrent={isCurrent}
            isCompleted={isCompleted}
            isLocked={isLocked}
            daysUntilUnlock={daysUntilUnlock}
            enrollmentId={enrollment?.id}
            onSaveReflection={enrollment ? onSaveReflection : null}
            reflectedWeeks={completedWeeks}
          />
        );
      })}
    </div>
  );
}

// ── Main Programs page ────────────────────────────────────────────────────────
export default function Programs() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth0();
  const [localUser, setLocalUser]     = useState(null);
  const [programs, setPrograms]       = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [activeProgram, setActiveProgram] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [enrolling, setEnrolling]     = useState(false);
  const [reEnrolling, setReEnrolling] = useState(false);

  const userId = authUser?.sub || localUser?.user_id || localUser?.email;

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try { setLocalUser(JSON.parse(stored)); } catch { /* noop */ }
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      setPrograms(STATIC_PROGRAMS);
      setLoading(false);
      return;
    }
    loadData();
  }, [userId]); // eslint-disable-line

  const loadData = async () => {
    try {
      const [progRes, enrollRes] = await Promise.all([
        axios.get(`${API}/programs`),
        axios.get(`${API}/programs/enrollment/${encodeURIComponent(userId)}`),
      ]);
      setPrograms(progRes.data.programs || STATIC_PROGRAMS);
      setEnrollments(enrollRes.data.enrollments || []);
    } catch {
      setPrograms(STATIC_PROGRAMS);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (programId) => {
    if (!userId) { toast.error('Please sign in to start a program.'); return; }
    setEnrolling(true);
    try {
      const res = await axios.post(`${API}/programs/enroll`, {
        user_id: userId, program_id: programId,
      });
      const updated = res.data;
      setEnrollments(prev => [...prev.filter(e => e.program_id !== programId), updated]);
      const prog = programs.find(p => p.id === programId);
      if (prog) setActiveProgram({ program: prog, enrollment: updated });
      if (updated.needs_baseline) {
        toast('First: a 2-minute baseline check-in — so your program can measure real change.', { duration: 6000 });
        setTimeout(() => navigate('/assessment'), 1400);
      } else {
        toast.success('Program started! Your journey begins now.');
      }
    } catch {
      toast.error('Could not start program. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const handleReEnroll = async (programId) => {
    if (!userId) return;
    setReEnrolling(true);
    try {
      const res = await axios.post(`${API}/programs/enroll`, {
        user_id: userId, program_id: programId, re_enroll: true,
      });
      const updated = res.data;
      setEnrollments(prev => [...prev.filter(e => e.program_id !== programId), updated]);
      toast.success('Program restarted. Fresh clock, fresh start.');
      const prog = programs.find(p => p.id === programId);
      if (prog) setActiveProgram({ program: prog, enrollment: updated });
    } catch {
      toast.error('Could not restart. Please try again.');
    } finally {
      setReEnrolling(false);
    }
  };

  const handleSaveReflection = async (enrollmentId, reflection) => {
    try {
      const res = await axios.post(`${API}/programs/enrollment/${enrollmentId}/advance`, { reflection });
      const updated = res.data;
      setEnrollments(prev => prev.map(e => e.id === enrollmentId ? { ...e, ...updated } : e));
      if (activeProgram?.enrollment?.id === enrollmentId) {
        setActiveProgram(prev => ({ ...prev, enrollment: { ...prev.enrollment, ...updated } }));
      }
      toast.success('Reflection saved.');
    } catch {
      toast.error('Could not save. Please try again.');
    }
  };

  const enrollmentFor = (programId) => enrollments.find(e => e.program_id === programId);

  // ── Program detail view ──────────────────────────────────────────────────
  if (activeProgram) {
    const { program, enrollment } = activeProgram;
    const meta  = PROGRAM_META[program.id] || {};
    const color = meta.color || TEAL;
    const isEnrolled = !!enrollment;

    return (
      <div style={{ minHeight: '100vh', background: CREAM, padding: '40px 20px 100px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <button
            onClick={() => setActiveProgram(null)}
            style={{ background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: '0.9rem', marginBottom: '20px', padding: 0 }}
          >
            ← All programs
          </button>

          {/* Header card */}
          <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '20px', padding: '24px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(30,107,104,0.1)' }}>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '14px',
                background: color + '18',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {meta.Icon && <meta.Icon size={30} color={color} />}
              </div>
              <div>
                <h2 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: '#1E3A3A', fontSize: '1.35rem', lineHeight: 1.2 }}>
                  {program.title}
                </h2>
                {isEnrolled ? (
                  <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.82rem' }}>
                    {enrollment.status === 'completed'
                      ? '8 weeks complete · well done'
                      : `Day ${enrollment.current_day} of 56 · Week ${enrollment.current_week} of 8`}
                  </p>
                ) : (
                  <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.82rem' }}>
                    8 weeks · somatic-first · evidence-based
                  </p>
                )}
              </div>
            </div>

            {isEnrolled && enrollment.status !== 'completed' && (
              <>
                {/* Progress bar */}
                <div style={{ height: '8px', background: '#F0F0F0', borderRadius: '4px', marginBottom: '6px' }}>
                  <div style={{
                    height: '100%', width: `${enrollment.total_progress_pct || 0}%`,
                    background: color, borderRadius: '4px', transition: 'width 0.5s',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#888', marginBottom: '14px' }}>
                  <span>{enrollment.total_progress_pct || 0}% complete</span>
                  {enrollment.days_until_next_week > 0 && (
                    <span>Week {enrollment.current_week + 1} unlocks in {enrollment.days_until_next_week} day{enrollment.days_until_next_week !== 1 ? 's' : ''}</span>
                  )}
                </div>

                {/* Week progress mini-row */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
                  {[1,2,3,4,5,6,7,8].map(w => (
                    <div key={w} style={{
                      flex: 1, height: '6px', borderRadius: '3px',
                      background: w < enrollment.current_week
                        ? color
                        : w === enrollment.current_week
                        ? color + '66'
                        : '#EEE',
                      transition: 'background 0.3s',
                    }} />
                  ))}
                </div>
              </>
            )}

            {/* Not enrolled — CTA */}
            {!isEnrolled && (
              <>
                <p style={{ margin: '0 0 10px', color: '#5A7A77', fontSize: '0.88rem', lineHeight: 1.65 }}>
                  {meta.desc}
                </p>
                {meta.detail && (
                  <p style={{ margin: '0 0 20px', color: '#8FA3A0', fontSize: '0.82rem', lineHeight: 1.7 }}>
                    {meta.detail}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                  {['Weekly challenge', 'Daily practice', 'Reflection journal'].map(tag => (
                    <span key={tag} style={{
                      fontSize: '0.7rem', padding: '4px 10px',
                      background: `${color}12`, color,
                      borderRadius: 20, fontWeight: '600', letterSpacing: '0.03em',
                    }}>{tag}</span>
                  ))}
                </div>
                <button
                  onClick={() => handleEnroll(program.id)}
                  disabled={enrolling}
                  style={{
                    width: '100%', padding: '14px',
                    background: enrolling ? 'rgba(30,107,104,0.2)' : `linear-gradient(135deg, ${color}, ${color}CC)`,
                    color: '#fff', border: 'none', borderRadius: '14px',
                    fontSize: '0.92rem', fontWeight: '600',
                    cursor: enrolling ? 'not-allowed' : 'pointer',
                    boxShadow: enrolling ? 'none' : '0 4px 16px rgba(30,107,104,0.25)',
                    transition: 'all 0.2s',
                  }}
                >
                  {enrolling ? 'Starting…' : 'Begin this program →'}
                </button>
              </>
            )}

            {/* Re-enroll button (enrolled programs) */}
            {isEnrolled && (
              <button
                onClick={() => handleReEnroll(program.id)}
                disabled={reEnrolling}
                style={{
                  width: '100%', padding: '10px',
                  background: 'none',
                  border: `1px solid ${color}44`,
                  borderRadius: '10px',
                  color,
                  fontSize: '0.82rem', fontWeight: '600',
                  cursor: reEnrolling ? 'not-allowed' : 'pointer',
                  opacity: reEnrolling ? 0.6 : 1,
                  transition: 'all 0.2s',
                  marginTop: enrollment.status === 'completed' ? 0 : 8,
                }}
              >
                {reEnrolling ? 'Restarting…' : enrollment.status === 'completed' ? 'Start again →' : 'Restart from Day 1'}
              </button>
            )}
          </div>

          {/* Outcome + presence (enrolled only) */}
          {isEnrolled && <ProgramOutcomeCard enrollmentId={enrollment.id} color={color} />}

          {/* Week cards */}
          {isEnrolled && (
            <ProgramWeeks
              programId={program.id}
              enrollment={enrollment}
              color={color}
              onSaveReflection={(reflection) => handleSaveReflection(enrollment.id, reflection)}
            />
          )}

          {/* Not enrolled — preview weeks */}
          {!isEnrolled && (
            <ProgramWeeks
              programId={program.id}
              enrollment={null}
              color={color}
              onSaveReflection={null}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Main list view ───────────────────────────────────────────────────────
  const activeEnrollments   = enrollments.filter(e => e.status === 'active');
  const completedEnrollments = enrollments.filter(e => e.status === 'completed');
  const availablePrograms   = programs.filter(p => !enrollmentFor(p.id));

  return (
    <div style={{ minHeight: '100vh', background: CREAM, padding: '40px 20px 100px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: TEAL, cursor: 'pointer', fontSize: '0.9rem', marginBottom: '20px', padding: 0 }}
        >
          ← Back
        </button>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", color: TEAL, fontSize: '2rem', marginBottom: '8px' }}>
            Structured Programs
          </h1>
          <p style={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.6 }}>
            8-week therapeutic journeys. Evidence-based, somatic-first.<br/>
            <span style={{ fontSize: '0.82rem', color: '#AABAB8' }}>
              Each week unlocks automatically as real time passes.
            </span>
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#AAB8B6', padding: '40px' }}>Loading…</div>
        ) : (
          <>
            {/* Active programs */}
            {activeEnrollments.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.1em', color: TEAL, textTransform: 'uppercase', marginBottom: '14px' }}>
                  Active now
                </p>
                {activeEnrollments.map(enrollment => {
                  const prog = programs.find(p => p.id === enrollment.program_id);
                  if (!prog) return null;
                  return (
                    <ProgramCard
                      key={enrollment.id}
                      program={prog}
                      enrollment={enrollment}
                      onOpen={(p, e) => setActiveProgram({ program: p, enrollment: e })}
                    />
                  );
                })}
              </div>
            )}

            {/* Completed programs */}
            {completedEnrollments.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.1em', color: '#888', textTransform: 'uppercase', marginBottom: '14px' }}>
                  Completed
                </p>
                {completedEnrollments.map(enrollment => {
                  const prog = programs.find(p => p.id === enrollment.program_id);
                  if (!prog) return null;
                  return (
                    <ProgramCard
                      key={enrollment.id}
                      program={prog}
                      enrollment={enrollment}
                      onOpen={(p, e) => setActiveProgram({ program: p, enrollment: e })}
                    />
                  );
                })}
              </div>
            )}

            {/* Available programs */}
            {availablePrograms.length > 0 && (
              <div>
                <p style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.1em', color: '#888', textTransform: 'uppercase', marginBottom: '14px' }}>
                  {activeEnrollments.length > 0 ? 'Also available' : 'Choose a program'}
                </p>
                {availablePrograms.map(prog => (
                  <ProgramCard
                    key={prog.id}
                    program={prog}
                    enrollment={null}
                    onOpen={(p) => setActiveProgram({ program: p, enrollment: null })}
                  />
                ))}
              </div>
            )}

            {/* All programs enrolled */}
            {availablePrograms.length === 0 && activeEnrollments.length === 0 && completedEnrollments.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#AAB8B6' }}>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>
                  No programs available. Check back soon.
                </p>
              </div>
            )}
          </>
        )}

        {/* Circles CTA */}
        <div
          onClick={() => navigate('/circles')}
          style={{
            margin: '28px 0 8px',
            background: 'linear-gradient(135deg, rgba(30,107,104,0.05), rgba(184,144,106,0.05))',
            border: '1px solid rgba(30,107,104,0.14)',
            borderRadius: '18px', padding: '18px 20px',
            cursor: 'pointer', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,107,104,0.10)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#2D8B87', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>
                Integration Circles
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#4A6A66', lineHeight: 1.55 }}>
                See what others in your program are discovering. Anonymous collective wisdom, distilled.
              </p>
            </div>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ marginLeft: 14, flexShrink: 0 }}>
              <circle cx="9" cy="9" r="7" stroke="#2D8B87" strokeWidth="1.4" fill="none"/>
              <circle cx="9" cy="9" r="2.5" fill="#2D8B87" opacity="0.5"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
